import React from 'react';
import { useLocation } from 'react-router-dom';

import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { PageLayout } from 'src/components/common/pageLayout/PageLayout';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { URLModal } from 'src/components/common/URLModal';
import { GraphSetupModal } from 'src/components/graphSetupModal/GraphSetupModal';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { PublishYourSchemaModal as LegacyPublishYourSchemaModal } from 'src/components/publishSchema/legacy/LegacyPublishYourSchemaModal';
import { PublishYourSchemaModal } from 'src/components/publishSchema/PublishYourSchemaModal';
import Config from 'src/lib/config';
import {
  useShouldEnableFed2Onboarding,
  useShowApiKeysTab,
} from 'src/lib/featureFlags';
import { getDaysRemainingOnTrial } from 'src/lib/getDaysRemainingOnTrial';
import { BillingPlanKind } from 'src/lib/graphqlTypes/types';

import { PlanBadge } from '../orgSettingsPage/components/PlanBadge/PlanBadge';
import * as orgRoutes from '../routes';

interface Props {
  accountId: string;
  accountName: string;
  trialEndDate: string | number | null | undefined;
  children: React.ReactNode;
  planName?: string;
  planKind?: BillingPlanKind;
}

export const TabbedAccountLayout = ({
  accountId,
  accountName,
  planName,
  planKind,
  trialEndDate,
  children,
}: Props) => {
  const location = useLocation();

  const ffFed2Onboarding = useShouldEnableFed2Onboarding();

  const showApiKeysTab = useShowApiKeysTab();

  const daysRemaining = trialEndDate
    ? getDaysRemainingOnTrial(trialEndDate)
    : 0;

  return (
    <PageLayout
      title={
        <PageTitle
          title={`${accountName ?? '...'}`}
          tabs={
            <>
              <HeaderTab
                tabName="Graphs"
                to={orgRoutes.graphs.locationFrom({
                  location,
                  fromRouteConfig: orgRoutes.org,
                })}
                tabID={Config.orgLayoutTabs.Supergraphs}
              />
              <PermissionGuard
                accountId={accountId}
                accountPermissions={['canQueryStats', 'canReadTickets']}
                fallback={null}
              >
                <HeaderTab
                  tabName="Dashboard"
                  to={orgRoutes.dashboard.locationFrom({
                    location,
                    fromRouteConfig: orgRoutes.org,
                  })}
                  tabID={Config.orgLayoutTabs.ChampionDashboard}
                />
              </PermissionGuard>
              <PermissionGuard
                accountId={accountId}
                accountPermissions="canQueryMembers"
                fallback={null}
              >
                <HeaderTab
                  tabName="Members"
                  to={orgRoutes.members.locationFrom({
                    location,
                    fromRouteConfig: orgRoutes.org,
                  })}
                  tabID={Config.orgLayoutTabs.Management}
                />
              </PermissionGuard>
              <PermissionGuard
                accountId={accountId}
                accountPermissions={[
                  'canQueryBillingInfo',
                  'canSetConstrainedPlan',
                  'canUpdateBillingInfo',
                  'canUpdateMetadata',
                ]}
                fallback={null}
              >
                <HeaderTab
                  tabName="Organization"
                  to={orgRoutes.settings.locationFrom({
                    location,
                    fromRouteConfig: orgRoutes.org,
                  })}
                  tabID={Config.orgLayoutTabs.Plan}
                />
              </PermissionGuard>
              {showApiKeysTab && (
                <PermissionGuard
                  accountId={accountId}
                  accountPermissions="canQueryMembers"
                  fallback={null}
                >
                  <HeaderTab
                    tabName="API Keys"
                    to={orgRoutes.apiKeys.locationFrom({
                      location,
                      fromRouteConfig: orgRoutes.org,
                    })}
                    tabID={Config.orgLayoutTabs.ApiKeys}
                  />
                </PermissionGuard>
              )}
              <PermissionGuard
                accountId={accountId}
                accountPermissions="canAudit"
                fallback={null}
              >
                <HeaderTab
                  tabName="Audit"
                  to={orgRoutes.usage.locationFrom({
                    location,
                    fromRouteConfig: orgRoutes.org,
                  })}
                  tabID={Config.orgLayoutTabs.AuditLogs}
                />
              </PermissionGuard>
            </>
          }
        >
          {accountName ?? '...'}
          {planName && (
            <PlanBadge
              accountId={accountId}
              planName={planName}
              planKind={planKind}
              daysRemaining={daysRemaining}
            />
          )}
        </PageTitle>
      }
    >
      <URLModal name={Config.modals.createGraph}>
        {({ closeModal }) => (
          <GraphSetupModal accountId={accountId} onClose={closeModal} />
        )}
      </URLModal>
      <URLModal name={Config.modals.publishYourSchema}>
        {({ isOpen, closeModal }) =>
          ffFed2Onboarding ? (
            <PublishYourSchemaModal isOpen={isOpen} onClose={closeModal} />
          ) : (
            <LegacyPublishYourSchemaModal
              isOpen={isOpen}
              onClose={closeModal}
            />
          )
        }
      </URLModal>

      {children}
    </PageLayout>
  );
};
